'use client';
import React, { useState } from 'react';
import { UserPlus, Trash2, Mail, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'executive' | 'sales' | 'operations' | 'analyst';
  status: 'active' | 'invited' | 'suspended';
  lastActive: string;
  joinedDate: string;
}

const initialTeam: TeamMember[] = [
  { id: 'tm-001', name: 'Alex Mercer', email: 'alex.mercer@nexusmetrics.io', role: 'executive', status: 'active', lastActive: 'Now', joinedDate: 'Jan 12, 2025' },
  { id: 'tm-002', name: 'Priya Nair', email: 'priya.nair@nexusmetrics.io', role: 'sales', status: 'active', lastActive: '2h ago', joinedDate: 'Mar 04, 2025' },
  { id: 'tm-003', name: 'Marcus Webb', email: 'marcus.webb@nexusmetrics.io', role: 'operations', status: 'active', lastActive: '1d ago', joinedDate: 'Feb 18, 2025' },
  { id: 'tm-004', name: 'Sofia Reyes', email: 'sofia.reyes@nexusmetrics.io', role: 'sales', status: 'active', lastActive: '3h ago', joinedDate: 'Apr 01, 2025' },
  { id: 'tm-005', name: 'Jordan Okafor', email: 'jordan.okafor@nexusmetrics.io', role: 'analyst', status: 'active', lastActive: '5h ago', joinedDate: 'Apr 22, 2025' },
  { id: 'tm-006', name: 'Lena Fischer', email: 'lena.fischer@nexusmetrics.io', role: 'sales', status: 'invited', lastActive: 'Never', joinedDate: 'May 10, 2025' },
  { id: 'tm-007', name: 'Kenji Tanaka', email: 'kenji.tanaka@nexusmetrics.io', role: 'operations', status: 'suspended', lastActive: '14d ago', joinedDate: 'Jan 30, 2025' },
];

const roleConfig = {
  executive: { label: 'Executive', class: 'bg-primary/10 text-primary' },
  sales: { label: 'Sales Manager', class: 'bg-positive/10 text-positive' },
  operations: { label: 'Operations', class: 'bg-warning/10 text-warning' },
  analyst: { label: 'Analyst', class: 'bg-info/10 text-info' },
};

const statusConfig = {
  active: { label: 'Active', class: 'bg-positive/10 text-positive' },
  invited: { label: 'Invited', class: 'bg-warning/10 text-warning' },
  suspended: { label: 'Suspended', class: 'bg-negative/10 text-negative' },
};

export default function TeamAccessSettings() {
  const [team, setTeam] = useState<TeamMember[]>(initialTeam);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleRoleChange = (id: string, newRole: TeamMember['role']) => {
    setTeam((prev) => prev.map((m) => m.id === id ? { ...m, role: newRole } : m));
    toast.success('Role updated');
  };

  const handleRemove = (id: string) => {
    const member = team.find((m) => m.id === id);
    setTeam((prev) => prev.filter((m) => m.id !== id));
    setDeleteId(null);
    if (member) toast.success(`${member.name} removed from team`);
  };

  const handleResendInvite = (name: string) => {
    // Backend integration point: POST /api/team/invite/resend
    toast.success(`Invite resent to ${name}`);
  };

  return (
    <div className="space-y-5">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-foreground">Team Members</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {team.filter((m) => m.status === 'active').length} active · {team.filter((m) => m.status === 'invited').length} pending invite
            </p>
          </div>
          <button
            onClick={() => toast.info('Invite member modal — connect to invite API')}
            className="flex items-center gap-2 px-3.5 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors btn-press"
          >
            <UserPlus size={15} />
            Invite Member
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2.5 px-3 label-caps text-muted-foreground">Member</th>
                <th className="text-left py-2.5 px-3 label-caps text-muted-foreground">Role</th>
                <th className="text-left py-2.5 px-3 label-caps text-muted-foreground hidden md:table-cell">Status</th>
                <th className="text-left py-2.5 px-3 label-caps text-muted-foreground hidden lg:table-cell">Last Active</th>
                <th className="text-left py-2.5 px-3 label-caps text-muted-foreground hidden lg:table-cell">Joined</th>
                <th className="text-right py-2.5 px-3 label-caps text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {team.map((member) => {
                const role = roleConfig[member.role];
                const status = statusConfig[member.status];
                const initials = member.name.split(' ').map((n) => n[0]).join('');
                return (
                  <tr key={member.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors group">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary">{initials}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value as TeamMember['role'])}
                        className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring ${role.class}`}
                        aria-label={`Change role for ${member.name}`}
                      >
                        <option value="executive">Executive</option>
                        <option value="sales">Sales Manager</option>
                        <option value="operations">Operations</option>
                        <option value="analyst">Analyst</option>
                      </select>
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell">
                      <span className={`label-caps rounded-full px-2.5 py-1 ${status.class}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="py-3 px-3 hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground">{member.lastActive}</span>
                    </td>
                    <td className="py-3 px-3 hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground">{member.joinedDate}</span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end gap-1">
                        {member.status === 'invited' && (
                          <button
                            onClick={() => handleResendInvite(member.name)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors btn-press opacity-0 group-hover:opacity-100"
                            aria-label={`Resend invite to ${member.name}`}
                          >
                            <Mail size={13} />
                          </button>
                        )}
                        {deleteId === member.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleRemove(member.id)}
                              className="px-2 py-1 text-xs font-semibold bg-negative text-white rounded-lg hover:bg-negative/90 transition-colors btn-press"
                            >
                              Remove
                            </button>
                            <button
                              onClick={() => setDeleteId(null)}
                              className="px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteId(member.id)}
                            disabled={member.name === 'Alex Mercer'}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-negative hover:bg-negative/10 transition-colors btn-press opacity-0 group-hover:opacity-100 disabled:opacity-20 disabled:cursor-not-allowed"
                            aria-label={`Remove ${member.name} from team`}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permissions info */}
      <div className="card-surface p-5 border-l-4 border-primary">
        <div className="flex items-start gap-3">
          <Shield size={18} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-foreground mb-1">Role-Based Access Control</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {[
                { role: 'Executive', access: 'Full dashboard access, all metrics, team data, export reports' },
                { role: 'Sales Manager', access: 'Sales, funnel, team leaderboard, product performance' },
                { role: 'Operations', access: 'Inventory, support tickets, scheduled reports, integrations' },
                { role: 'Analyst', access: 'Read-only access to all analytics, no team or settings access' },
              ].map((item) => (
                <div key={`perm-${item.role}`} className="text-xs">
                  <span className="font-semibold text-foreground">{item.role}:</span>{' '}
                  <span className="text-muted-foreground">{item.access}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}